import * as p from '@clack/prompts'
import type { RailwayAuthResult, RailwayRunner } from '@cli-types/next-adapter/railway.js'
import { LOGIN_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { spinner } from '@cli-utils/terminal.js'
import { runRailway } from '@next-adapter/init/railway/runner/run-railway.js'
import pc from 'picocolors'
import { checkRailwayAuth } from './check-railway-auth.js'
import { checkRailwayProjectToken } from './check-railway-project-token.js'

export async function ensureRailwayAuth(
  runner: RailwayRunner,
  cwd: string,
  options: {
    interactive: boolean
    env?: NodeJS.ProcessEnv
    quietIfAuthed?: boolean
  }
): Promise<RailwayAuthResult> {
  const env = options.env ?? process.env
  const checkSpinner = spinner()
  checkSpinner.start(
    runner.source === 'npx' ? 'Checking Railway, This may take a moment' : 'Checking Railway'
  )
  const projectTokenWasProvided = Boolean(env.RAILWAY_TOKEN)
  const existing = projectTokenWasProvided
    ? await checkRailwayProjectToken(runner, cwd, env)
    : await checkRailwayAuth(runner, cwd, env)

  if (existing.authed) {
    if (options.quietIfAuthed) {
      checkSpinner.clear()
    } else {
      checkSpinner.stop(
        existing.account?.email
          ? `Signed in to Railway as ${pc.cyan(existing.account.email)}`
          : 'Signed in to Railway'
      )
    }
    return existing
  }

  const tokenWasProvided = projectTokenWasProvided || Boolean(env.RAILWAY_API_TOKEN)
  if (tokenWasProvided) {
    checkSpinner.stop('Could not verify the Railway token')
    return {
      authed: false,
      reason: existing.reason === 'timeout' ? 'timeout' : 'invalid-token'
    }
  }

  if (!options.interactive) {
    checkSpinner.clear()
    return { authed: false, reason: existing.reason }
  }

  checkSpinner.clear()
  p.log.info(
    'Sign in to Railway to continue. Railway will open your browser or show a device code.'
  )
  const login = await runRailway(runner, ['login'], {
    cwd,
    mode: 'inherit',
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'cancelled' }
  }

  const verifySpinner = spinner()
  verifySpinner.start('Verifying Railway sign-in')
  const verified = await checkRailwayAuth(runner, cwd, env)
  if (verified.authed) {
    verifySpinner.stop(
      verified.account?.email
        ? `Signed in to Railway as ${pc.cyan(verified.account.email)}`
        : 'Signed in to Railway'
    )
    return verified
  }

  verifySpinner.stop('Could not verify your Railway sign-in')
  return { authed: false, reason: 'failed' }
}
